import React, { useState, useMemo } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import TableSearchBar from "../../Main/components/CareManager/TableSearchBar";
import { DOCUMENT_CATEGORIES } from "../common/constants";
import { formatDate } from "../../../utils/formatters/date";

const CATEGORY_OPTIONS = ["All", ...DOCUMENT_CATEGORIES];

const CATEGORY_COLORS = {
  Certification: { text: "#1565C0", bg: "#E3F2FD" },
  Contract: { text: "#2E7D32", bg: "#E8F5E9" },
  Evaluation: { text: "#E65100", bg: "#FFF3E0" },
  Training: { text: "#6A1B9A", bg: "#F3E5F5" },
  License: { text: "#00838F", bg: "#E0F7FA" },
  Other: { text: "#546E7A", bg: "#ECEFF1" },
};

/**
 * Formato legible para tamaño de archivo.
 */
const formatSize = (bytes) => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * DataGrid con lista de documentos de empleados.
 *
 * @param {{ documents: Array, onViewDetails: (doc) => void, onDelete: (id) => void }} props
 */
export default function DocumentsTable({ documents = [], onViewDetails, onDelete }) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  const filteredRows = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return documents.filter((doc) => {
      if (lowerSearch && !doc.fileName?.toLowerCase().includes(lowerSearch)) return false;
      if (categoryFilter !== "All" && doc.category !== categoryFilter) return false;
      return true;
    });
  }, [documents, search, categoryFilter]);

  const columns = [
    {
      field: "fileName",
      headerName: "File Name",
      flex: 1,
      minWidth: 200,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} noWrap title={value}>
          {value}
        </Typography>
      ),
    },
    {
      field: "fileType",
      headerName: "Type",
      width: 140,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap title={value}>
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "fileSize",
      headerName: "Size",
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }) => (
        <Typography variant="body2">{formatSize(value)}</Typography>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      width: 130,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const colors = CATEGORY_COLORS[value] || CATEGORY_COLORS.Other;
        return (
          <Chip
            label={value}
            size="small"
            sx={{
              fontWeight: 600,
              color: colors.text,
              bgcolor: colors.bg,
            }}
          />
        );
      },
    },
    {
      field: "uploadedByName",
      headerName: "Uploaded By",
      width: 160,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap>
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "uploadedAt",
      headerName: "Date",
      width: 160,
      renderCell: ({ value }) => (
        <Typography variant="body2">
          {formatDate(value, "-")}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => onViewDetails?.(row)}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Details
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => onDelete?.(row.id)}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <TableSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by file name..."
        filters={[
          {
            key: "category",
            label: "Category",
            options: CATEGORY_OPTIONS,
            value: categoryFilter,
          },
        ]}
        onFilterChange={(key, value) => {
          if (key === "category") setCategoryFilter(value);
        }}
      />

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.id}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[5, 10, 25]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.78rem",
          },
        }}
      />
    </Box>
  );
}
